import { css } from '@emotion/react'
import styled from '@emotion/styled'
import {
    CSSProperties,
    ForwardedRef,
    forwardRef,
    KeyboardEvent,
    memo,
    MutableRefObject,
    RefObject,
    useCallback,
    useEffect,
    useState,
} from 'react'
import { openMenuDialog } from '../../../actions/menu'
import {
    deleteNote,
    saveNoteDebounced,
    SearchNotesResults,
} from '../../../actions/notes'
import { MfcUserNotepad } from '../../../libraries/mfc/MfcUserNotepad'
import { fetchNotes } from '../../../mfcLogic/api/myNotes'
import i18n from '../../../utilities/i18n'
import ButtonDelete from '../../Buttons/ButtonDelete'
import ButtonInsertDate from '../../Buttons/ButtonInsertDate'
import { LoadingRoot } from '../../common/LoadingRoot'
import { AppStateType, useAppStateSelector } from '../../Hooks/useAppState'
import { useUserSelector } from '../../Hooks/useUser'
import { getThemeSpacing } from '../../styles/appTheme'
import APP_BREAKPOINTS, { maxWidth } from '../../styles/breakpoints'
import { palette } from '../../styles/colors'
import shape from '../../styles/shape'
import CircularProgress from '../CircularProgress'
import NoteName from './NoteName'
import SearchResults from './SearchResults'

const NotesPanelRoot = styled('div')({
    display: 'flex',
    width: '100%',
    height: '100%',
    flex: 'auto',
    flexFlow: 'column nowrap',
    alignItems: 'flex-start',
    overflowY: 'scroll',
    // ...NO_SCROLLBAR_STYLES,
})
const ClearedNotesPanelRoot = styled('div')({
    display: 'flex',
    width: '100%',
    flexFlow: 'row wrap',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: getThemeSpacing(0.5),
    padding: getThemeSpacing(2),
    background: palette.drawerDivider,
})
const EmptyRoot = styled('div')({
    display: 'flex',
    flexFlow: 'column nowrap',
    height: '100%',
    width: '100%',
    justifyContent: 'center',
    alignItems: 'center',
})
const PanelHeaderRoot = styled('div')({
    display: 'flex',
    position: 'relative',
    width: '100%',
    flexFlow: 'row wrap',
    alignItems: 'center',
    justifyContent: 'space-between',
    background: palette.drawerDivider,
    paddingTop: getThemeSpacing(0.5),
    paddingBottom: getThemeSpacing(0.5),
    marginBottom: getThemeSpacing(0.5),
})
const PanelHeaderItem = styled('div')({
    flex: 'none',
    marginLeft: getThemeSpacing(1),
})
const PanelHeaderButtons = styled('div')({
    display: 'flex',
    flexFlow: 'row nowrap',
    alignItems: 'center',
    justifyContent: 'space-between',
})
const LastLoggedIn = styled('div')({
    position: 'absolute',
    top: 33,
    left: 56,
    fontSize: 9,
})
const NotesListRoot = styled('div')({
    display: 'flex',
    width: '100%',
    height: '100%',
    overflowY: 'scroll',
    padding: getThemeSpacing(0.2),
    // ...NO_SCROLLBAR_STYLES,
})
const NotesWrapper = styled('div')({
    display: 'flex',
    width: '100%',
    height: '100%',
    flexFlow: 'row wrap',
    alignItems: 'baseline',
    padding: `${getThemeSpacing(0.25)} ${getThemeSpacing(0.5)}`,
})
const NotesBody = styled('textarea')({
    width: '100%',
    height: '100%',
    resize: 'none',
    fontFamily: 'inherit',
    color: palette.primaryText,
    backgroundColor: palette.background,
    borderColor: palette.drawerDivider,
    outline: 'none',
    '&:focus': {
        outline: 'none',
    },
    '&:focus-visible': {
        outline: 'none',
    },
})
const StatusMessage = styled('div')({
    display: 'flex',
    flexFlow: 'row wrap',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0.8125rem',
    padding: getThemeSpacing(0.5),
    marginRight: getThemeSpacing(1),
    borderRadius: shape.borderRadius,
    backgroundColor: palette.statusMessage,
    color: '#000',
    border: 'none',
    opacity: 1,
    willChange: 'opacity',
    transition: 'opacity 250ms ease-out 0ms',
    whiteSpace: 'pre',
})
const embeddedRootStyles = css({
    paddingBottom: `0 !important`,
})
const embeddedBodyStyles = css({
    minHeight: 150,
    maxHeight: 150,
    [maxWidth(APP_BREAKPOINTS.s)]: {
        minHeight: 75,
        maxHeight: 75,
    },
})

interface NoteState {
    note: string
    lastLogin: string
}

interface INotesPanelHeader {
    getList?(): Promise<void>
    isXs?: boolean
    lastLogin?: string
    name?: string
    noName?: boolean
    notesBody: NoteState
    notesBodyRef: RefObject<HTMLTextAreaElement>
    onSelect?(userId: number, username?: string): void
    onStatusMessage(value?: string): void
    setNoteState(value: NoteState): void
    showStatus: boolean
    statusMessage?: string
    token: string
    userId?: number
    isOnline?: boolean
}

const NotesPanelHeader = memo((props: INotesPanelHeader) => {
    const {
        getList = () => {},
        onSelect = () => {},
        onStatusMessage,
        setNoteState,
        isXs,
        lastLogin,
        name: username,
        noName,
        notesBody,
        notesBodyRef,
        showStatus,
        statusMessage,
        isOnline,
        token,
        userId,
    } = props
    const slug = username?.toLowerCase()

    const onDeleteNote = useCallback(() => {
        deleteNote(token, userId)
            .then(() => {
                onStatusMessage('Note deleted')
                setNoteState(undefined)
                onSelect(undefined)
                getList()
            })
            .catch(() => {
                onStatusMessage('Error deleting note')
            })
    }, [token, userId])

    const onInsertDate = useCallback(() => {
        const date = MfcUserNotepad.generateEntryDate() + '\n\n'
        const currentNotes = notesBody?.note || ''
        const newNotes = date + currentNotes
        setNoteState({ ...notesBody, note: newNotes })
        if (notesBodyRef.current) {
            notesBodyRef.current.focus()
            setTimeout(() => {
                const position = date.length - 1
                notesBodyRef.current.setSelectionRange(position, position)
            }, 0)
        }
        if (userId) {
            saveNoteDebounced(token, userId, newNotes)
        }
    }, [notesBody, token, userId, notesBodyRef])

    const onOpenMenu = useCallback(() => {
        if (slug) {
            openMenuDialog(slug, { hideNextPrev: true })
        }
    }, [slug])

    if (!userId) return null

    return (
        <PanelHeaderRoot data-testid="MyNotes__PanelHeader">
            <PanelHeaderItem>
                {!noName && (
                    <NoteName
                        username={username}
                        userId={userId}
                        onClick={onOpenMenu}
                        isOnline={!!isOnline}
                        addBoxShadow
                    />
                )}
            </PanelHeaderItem>
            <PanelHeaderButtons>
                {!isXs && !!statusMessage && (
                    <StatusMessage
                        {...(!showStatus ? { style: { opacity: 0 } } : {})}
                    >
                        {statusMessage}
                    </StatusMessage>
                )}
                <ButtonInsertDate onClick={onInsertDate} />
                <ButtonDelete
                    title={i18n.t('chat.deleteNote')}
                    onClick={onDeleteNote}
                />
            </PanelHeaderButtons>
            {!noName && !!lastLogin && (
                <LastLoggedIn>last logged in: {lastLogin}</LastLoggedIn>
            )}
        </PanelHeaderRoot>
    )
})

interface INotesPanelBody {
    embedded?: boolean
    notesBody: NoteState
    onStatusMessage(value?: string): void
    setNoteState(value: NoteState): void
    token: string
    userId: number
    textareaStyle?: CSSProperties
    isDrawer?: boolean
}

const NotesPanelBody = memo(
    forwardRef(
        (props: INotesPanelBody, ref: ForwardedRef<HTMLTextAreaElement>) => {
            const {
                embedded,
                notesBody,
                onStatusMessage,
                setNoteState,
                token,
                userId,
                textareaStyle,
                isDrawer,
            } = props

            const onInput = useCallback(
                (e: KeyboardEvent<HTMLTextAreaElement>) => {
                    const notes = e.currentTarget.value
                    setNoteState({ ...notesBody, note: notes })
                    if (!userId || !token) {
                        onStatusMessage('Error saving note')
                        return
                    }
                    saveNoteDebounced(token, userId, notes)
                        .then(() => onStatusMessage('Note saved'))
                        .catch(() => onStatusMessage('Error saving note'))
                },
                [token, userId],
            )

            const onKeyDown = useCallback(
                (e: KeyboardEvent<HTMLTextAreaElement>) => {
                    // prevent arrow keys from browsing models if inside a menu
                    e.stopPropagation()
                },
                [],
            )

            return (
                <NotesListRoot
                    className="no-scrollbars"
                    data-testid="MyNotes__PanelBody"
                    style={isDrawer ? { height: '100%' } : undefined}
                >
                    <NotesWrapper
                        style={isDrawer ? { height: '100%' } : undefined}
                    >
                        <NotesBody
                            ref={ref}
                            css={embedded ? embeddedBodyStyles : undefined}
                            onInput={onInput}
                            style={textareaStyle}
                            onKeyDown={onKeyDown}
                            value={notesBody?.note || ''}
                        />
                    </NotesWrapper>
                </NotesListRoot>
            )
        },
    ),
)

interface INotesPanel {
    clear?: boolean
    embedded?: boolean
    getUserList?(): Promise<void>
    hasUsers?: boolean
    isXs?: boolean
    onSelect?(userId: number): void
    onStatusMessage(value?: string): void
    query?: string
    searchResults?: SearchNotesResults
    showStatus: boolean
    statusMessage?: string
    textareaStyle?: CSSProperties
    userId?: number
    username?: string
}

const NotesPanel = memo(
    forwardRef<HTMLTextAreaElement, INotesPanel>((props, ref) => {
        const token = useAppStateSelector(AppStateType.SESSION, 'token')
        const {
            clear,
            embedded,
            hasUsers,
            isXs,
            searchResults,
            showStatus,
            statusMessage,
            userId: id,
            username,
            getUserList,
            onSelect,
            onStatusMessage,
            query,
            textareaStyle,
        } = props
        const rootStyles = embedded ? embeddedRootStyles : undefined
        const isOnline = useUserSelector(id, 'isOnline')
        const name = useUserSelector(id, 'name')

        const foundName = name ?? username
        const [loading, setLoading] = useState(true)
        const [notesBody, setNoteState] = useState<NoteState>(undefined)

        useEffect(() => {
            if (!id) return
            // fallback for de-activated users; worker
            // only knows of models who exist so when checking for notes
            setLoading(true)
            fetchNotes(id).then(({ notes, lastLogin }) => {
                setNoteState({ note: notes, lastLogin })
                setLoading(false)
            })
        }, [id])

        if (
            !statusMessage &&
            !embedded &&
            !query &&
            (clear || (!id && !searchResults))
        ) {
            return (
                <ClearedNotesPanelRoot data-testid="MyNotes__PanelEmpty">
                    {i18n.t(
                        hasUsers
                            ? 'chat.notesSelectUser'
                            : 'chat.notesUserListEmpty',
                    )}
                </ClearedNotesPanelRoot>
            )
        }

        if (searchResults) {
            return (
                <NotesPanelRoot
                    className="no-scrollbars"
                    data-testid="MyNotes__PanelRoot"
                >
                    <SearchResults data={searchResults} onSelect={onSelect} />
                </NotesPanelRoot>
            )
        }

        if (loading) {
            return (
                <EmptyRoot css={rootStyles} data-testid="MyNotes__PanelRoot">
                    <LoadingRoot>
                        <CircularProgress disableShrink />
                    </LoadingRoot>
                </EmptyRoot>
            )
        }

        return (
            <NotesPanelRoot
                className="no-scrollbars"
                css={rootStyles}
                data-testid="MyNotes__PanelRoot"
            >
                <NotesPanelHeader
                    isXs={isXs}
                    token={token}
                    name={foundName}
                    userId={id}
                    noName={embedded}
                    notesBodyRef={ref as MutableRefObject<HTMLTextAreaElement>}
                    notesBody={notesBody}
                    lastLogin={!isOnline ? notesBody?.lastLogin : undefined}
                    showStatus={showStatus}
                    statusMessage={statusMessage}
                    getList={getUserList}
                    onSelect={onSelect}
                    onStatusMessage={onStatusMessage}
                    setNoteState={setNoteState}
                    isOnline={!!isOnline}
                />
                <NotesPanelBody
                    ref={ref}
                    token={token}
                    userId={id}
                    embedded={embedded}
                    notesBody={notesBody}
                    onStatusMessage={onStatusMessage}
                    setNoteState={setNoteState}
                    textareaStyle={textareaStyle}
                />
            </NotesPanelRoot>
        )
    }),
)

export default NotesPanel
